<template>
	<div class="pb-16">
		<Mango @update:data="setKit" class="flex flex-col w-full items-center" :query="{ depthLimit: 2 }">
			<Spinner v-if="!kit?.id" />
			<div v-else-if="!kit.products?.length" class="w-full h-full flex items-center justify-center">
				<div class="p-16 mt-16">
					<h1 class="text-ihh-midnight-gray text-2xl font-semibold text-center">There are no items in this kit.</h1>
				</div>
			</div>
			<template v-if="kit?.id">
				<div class="w-full max-w-8xl mb-12">
					<div class="p-4" style="font-family: 'Montserrat', sans-serif">
						<div class="md:mb-12 mt-4 md:mt-0">
							<div class="flex flex-col sm:flex-row items-center sm:items-top justify-center w-full gap-y-4">
								<div
									v-if="kit?.image?.url"
									class="flex w-full justify-center items-center py-6 max-w-xs bg-ihh-pink rounded-xl border border-ihh-gray border-2 sm:mr-6"
								>
									<img :src="kit.image.url" class="w-full justify-center flex items-center place-items-center max-w-28 rounded" />
								</div>
								<div class="flex flex-col items-center sm:items-start space-y-4">
									<div class="text-center sm:text-start w-full">
										<span class="text-xl sm:text-3xl font-semibold text-ihh-midnight-gray">{{ kit.name }}</span>
									</div>
									<div class="w-full flex justify-center sm:justify-start" :class="hasOutOfStockItems ? 'flex-col' : ''">
										<div :class="hasOutOfStockItems ? 'pb-2' : 'pb-0'">
											<button
												@click="downloadContentOnPage"
												class="text-ihh-slate-gray hover:text-ihh-gray cursor-pointer font-semibold text-sm tracking-wide underline"
											>
												Download Order Form
											</button>
										</div>
										<button
											v-if="hasOutOfStockItems"
											@click="scrollToOutOfStockItems"
											class="flex w-full max-w-52 justify-center items-center text-center text-ihh-white text-xs font-semibold bg-ihh-coral rounded py-1 capitalize"
										>
											Kit contains out of stock items
										</button>
									</div>
								</div>
							</div>
						</div>

						<div class="flex w-full justify-between py-6">
							<div class="flex w-full justify-start text-start text-lg md:text-2xl tracking-wide text-ihh-midnight-gray m-auto whitespace-nowrap">
								Included in your kit
							</div>
							<div class="flex w-full justify-end max-w-36 sm:max-w-xs">
								<button
									@click="resetQuantities"
									class="group flex-row w-full m-auto items-center justify-center text-center text-xs sm:text-sm font-semibold text-ihh-midnight-gray hover:text-ihh-white border-3 border-ihh-dark-salmon px-4 py-3 rounded-lg hover:bg-ihh-dark-salmon duration-100"
								>
									Reset quantities
								</button>
							</div>
						</div>

						<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<BirthKitItem
								v-for="p in kit.products?.filter((p) => !p?.optional)"
								:key="p.product.id"
								:product="p.product"
								:variant="p.variant"
								:quantity="p.quantity"
								@update:quantity="p.quantity = $event"
								@update:variant="p.variant = $event"
							/>
						</div>

						<div v-if="optionalProducts?.length">
							<div class="flex w-full justify-between py-6">
								<div class="flex w-full justify-start text-start text-lg md:text-2xl tracking-wide text-ihh-midnight-gray m-auto whitespace-nowrap">
									Optional items
								</div>
							</div>

							<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<BirthKitItem
									v-for="p in optionalProducts"
									:key="p.product.id"
									:product="p.product"
									:variant="p.variant"
									:quantity="p.quantity"
									@update:quantity="p.quantity = $event"
									@update:variant="p.variant = $event"
								/>
							</div>
						</div>

						<div id="outOfStockItems" v-if="hasOutOfStockItems">
							<div class="flex w-full justify-between py-6">
								<div class="flex w-full justify-start text-start text-lg md:text-2xl tracking-wide text-ihh-midnight-gray m-auto whitespace-nowrap">
									Out of Stock Items
								</div>
							</div>

							<div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<BirthKitItem
									v-for="p in outOfStockItems"
									:key="p.product.id"
									:product="p.product"
									:variant="p.variant"
									:quantity="p.quantity"
									@update:quantity="p.quantity = $event"
									@update:variant="p.variant = $event"
								/>
							</div>
						</div>
						<!-- End Of The Birth Kit Items -->

						<p class="text-center pt-16">
							Need to order more than 1 kit? Email us at <a class="text-ihh-dark-salmon underline" href="mailto:help@inhishands.com">help@inhishands.com</a>
							for further assistance.
							<br /><br />
							<template v-if="!kit.author?.id">
								Are you a midwife and this is your kit? Please
								<button @click="claimKit" class="text-ihh-dark-salmon hover:text-ihh-salmon underline">claim your kit</button>
								to make changes to your kit.
							</template>
						</p>
					</div>
				</div>

				<div v-if="kit?.id" class="flex w-full justify-center sticky bottom-0 z-10">
					<div
						class="flex w-full justify-center m-auto items-center w-full bg-ihh-white shadow-md px-4 py-10 sm:p-y rounded border border-ihh-gray/50 cursor-pointer"
					>
						<div class="flex w-full max-w-8xl justify-between">
							<div class="flex flex-col w-full justify-between hidden sm:block">
								<div class="text-2xl font-semibold text-ihh-midnight-gray max-w-md tracking-wide mr-6">{{ kit.name }}</div>
								<div v-if="hasOutOfStockItems" class="flex w-full justify-start">
									<button class="mt-2 text-xs text-center bg-ihh-coral py-1 px-3 text-ihh-white font-bold capitalize rounded-md">
										Kit contains out of stock items
									</button>
								</div>
							</div>
							<div class="flex w-full justify-between sm:justify-end items-center">
								<div class="flex-col text-ihh-midnight-gray text-center md:text-end mr-6">
									<div class="flex sm:flex-col md:flex-row">
										<div class="text-base xs:text-lg font-semibold mr-2">SUBTOTAL:</div>
										<div class="text-base xs:text-lg font-semibold tracking-widest">
											{{ subtotal?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
										</div>
									</div>

									<a @click="cartBirthKit" class="flex w-full justify-between whitespace-nowrap">
										<div class="max-w-32 flex w-full">
											<span class="flex w-full justif-end">
												<svg
													xmlns="http://www.w3.org/2000/svg"
													viewBox="0 0 18 18"
													width="16"
													height="16"
													stroke-width="1.75"
													stroke-linecap="round"
													stroke-linejoin="round"
													class="p-0 mr-1"
												>
													<path
														d="M9 15.75V15.75C5.27175 15.75 2.25 12.7283 2.25 9V9C2.25 5.27175 5.27175 2.25 9 2.25V2.25C12.7283 2.25 15.75 5.27175 15.75 9V9C15.75 12.7283 12.7283 15.75 9 15.75Z"
														class="fill-none stroke-ihh-dark-salmon hover:stroke-ihh-dark-salmon"
													/>
													<path d="M9 12.75V9H8.25" class="fill-none stroke-ihh-dark-salmon hover:stroke-ihh-dark-salmon" />
													<path
														d="M8.81175 6C8.70825 6 8.62425 6.084 8.625 6.1875C8.625 6.291 8.709 6.375 8.8125 6.375C8.916 6.375 9 6.291 9 6.1875C9 6.084 8.916 6 8.81175 6"
														class="sfill-none stroke-ihh-dark-salmon hover:stroke-ihh-dark-salmon"
													/>
												</svg>
											</span>
											<button @click="shippingModalOpen = true" class="text-ihh-dark-salmon hover:text-ihh-salmon underline text-xs font-semibold">
												Shipping and returns
											</button>
										</div>
									</a>
								</div>
								<div class="flex items-center">
									<button
										@click="addToBag"
										class="items-center text-sm text-ihh-midnight-gray border-ihh- bg-ihh-dark-salmon hover:bg-ihh-salmon transition-all rounded-lg px-6 py-2 sm:py-4 font-semibold text-center whitespace-nowrap"
									>
										Add this kit to my bag
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
		</Mango>

		<Modal v-if="shippingModalOpen" @hide="shippingModalOpen = false">
			<div class="prose">
				<h2>Shipping and Returns</h2>
				<p>Shipping cost is based on weight. Just add products to your bag and use the Shipping Calculator to view the different options.</p>
				<p>We offer free shipping on orders over $125 shipping to the lower 48 states</p>
				<p>We want you to be 100% satisfied with your purchase. Most items in new condition can be returned or exchanged within 90 days of purchase.</p>
			</div>
		</Modal>

		<Modal
			v-if="upsellModalOpen"
			@hide="upsellModalOpen = false"
			max-width="max-w-4xl"
			class="w-full md:w-auto h-screen md:h-auto"
			dialog-classes="!max-h-screen md:!max-h-[75vh] !h-screen md:!h-auto"
			v-slot="{ close }"
		>
			<div class="text-center prose mx-auto">
				<h2 class="my-4">The kit has been added to your bag.</h2>
				<div class="flex space-x-8 justify-center">
					<button @click="(close(), (store.showBag = true))" class="px-3 py-2 rounded-lg border-3 border-ihh-dark-salmon" href="/cart.php">View Bag</button>
					<button @click="close" class="px-3 py-2 bg-ihh-dark-salmon hover:bg-ihh-salmon transition-all rounded-lg text-ihh-midnight-gray font-semibold">
						Continue Shopping
					</button>
				</div>
				<h4 class="">You may also like these:</h4>
			</div>
			<div>
				<div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
					<Product v-for="item in upsellItems" :key="item.id" :product="item" />
				</div>
			</div>
		</Modal>

		<Modal v-if="showLogin" @hide="showLogin = false" max-width="max-w-4xl">
			<Login @loggedIn="claimKit" :small="true" />
		</Modal>
	</div>
</template>

<script>
import Product from '../products/product.vue'
import BirthKitItem from '../products/birth-kit-item.vue'
import Swal from 'sweetalert2'
import Modal from '../layout/modal.vue'
import Mango from '../../helpers/mango'
import axios from 'axios'
import Login from '../layout/login.vue'

export default {
	components: { Product, BirthKitItem, Modal, Login },
	inject: ['store'],
	data() {
		return {
			showLogin: false,
			upsellItems: [],
			shippingModalOpen: false,
			upsellModalOpen: false,
			originalKit: null,
			kit: null,
			hasOutOfStockItems: false, // Boolean indicating if the kit had out-of-stock items before filtering
			outOfStockItems: [], // Array of out-of-stock items from the kit before filtering
			cartIsEmpty: false,
			cartIsEmptyModal: false,
			removeBirthKit: false,
			birthKitToRemove: null,
			activeForm: null,
			pages: [1, 2, 3, 4],
			selectedPage: 1,
			cart: false,
			showMenu: false,
			loading: false,
			query: {
				depthLimit: 2,
				fields: ['id', 'name', 'image', 'products', 'products.product', 'products.variant', 'products.quantity', 'products.optional'],
			},
			graphql: `
                query {
                    birthKit(id: "${this.$route.params.id}") {
                        id
                        name
                        image {
                            url
                        }
                        inventoryLevel
                        inventoryTracking
                        products {
                            product {
                                id
                                name
                                currentPrice
                                inventoryLevel
                                availability
                                outOfStock
                                bulkPricingRules {
                                    quantityMin
                                    quantityMax
                                    type
                                    amount
                                }
                                images {
                                    url
                                }
                                productOptions {
                                    id
                                    name
                                    displayName
                                    sortOrder
                                    optionValues {
                                        id
                                        label
                                        isDefault
                                        valueData {
                                            imageUrl
                                        }
                                    }
                                }
                                variants {
                                    id
                                    purchasingDisabled
                                    inventoryLevel
                                    currentPrice
                                    optionValues {
                                        id
                                        label
                                        optionId
                                        optionDisplayName
                                    }
                                }
                            }
                            variant {
                                id
                                purchasingDisabled
                                inventoryLevel
                                currentPrice
                                optionValues {
                                    id
                                    label
                                    optionId
                                    optionDisplayName
                                }
                            }
                            quantity
                            optional
                        }
                    }
                }
            `,
		}
	},
	async created() {
		let upsellIds = [862, 4069, 2022, 3338, 3681, 3813, 1926, 3064, 3224, 3263, 3337, 3544]
		// this.upsellItems = await Mango.products({ search: { bcId: { $in: upsellIds } }, limit: 12 })

		try {
			const upsellItems = await axios.get(`${this.store.api}/controllers/bigCommerce/birthKits/upsellProducts`)
			this.upsellItems = upsellItems?.data
		} catch (error) {
			console.log('Error fetching birthKit upsell items')
		}
	},

	methods: {
		scrollToOutOfStockItems() {
			document.getElementById('outOfStockItems').scrollIntoView({ behavior: 'smooth' })
		},
		setKit(kit) {
			if (!kit) this.$router.push('/404')
			if (!kit.products) return (this.kit = kit)
			if (!kit?.name) {
				document.title = 'Custom Birth Kit By Midwife'
			} else {
				document.title = kit.name
			}
			kit = kit?.birthKit || kit

			kit.products = kit.products?.filter((p) => !!p.product)

			const unfilteredKit = JSON.parse(JSON.stringify(kit))
			this.hasOutOfStockItems = this.kitHasOutOfStockProducts(unfilteredKit)
			this.outOfStockItems = this.getOutOfStockProducts(unfilteredKit) || []

			// Filter out out-of-stock products
			const filteredProducts = kit.products
				.filter((item) => !this.productAndVariantsOutOfStock(item.product))
				.sort((a, b) => a.product?.name?.localeCompare(b.product?.name))
			this.kit = JSON.parse(JSON.stringify(kit))
			delete this.kit.products
			this.kit.products = filteredProducts

			// Store the original filtered kit
			this.originalKit = JSON.parse(JSON.stringify(this.kit))
		},
		resetQuantities() {
			Swal.fire({
				title: 'Are you sure?',
				text: 'This will reset all quantities to their original values',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Yes, reset',
				cancelButtonText: 'No, cancel',
			}).then((result) => {
				if (result.isConfirmed) {
					this.kit.products.forEach((p) => {
						p.quantity = this.originalKit.products.find((op) => op.product.id === p.product.id).quantity
					})
				}
			})
		},
		addToBag() {
			if (!this.requiredOptionsSelected?.value) {
				Swal.fire({
					title: 'Missing Required Options',
					html: `Select required options for the following products:<br><br><div style="text-align: left; display: inline-block;">${this.requiredOptionsSelected?.products.map((product) => `• ${product}`).join('<br>')}</div>`,
					icon: 'warning',
					confirmButtonText: 'Return to kit',
					confirmButtonColor: '#e59789',
				})
				return
			}

			let products = JSON.parse(JSON.stringify(this.kit.products))
			products = products.map((p) => ({ ...p, group: this.kit.id, item: p.product }))?.filter((p) => p.quantity > 0)

			// this.store.cart.cartItems = [...this.store.cart.cartItems, ...products] // update any existing items in the cart vs possibly adding duplicates

			let cartItems = [...this.store.cart.cartItems]

			for (const kitItem of products) {
				const matchIndex = cartItems.findIndex((cartItem) => {
					const productMatch = cartItem.product?.bcId === kitItem.product?.bcId
					const kitHasVariant = kitItem?.variant && kitItem?.variant?.bcId
					const cartHasVariant = cartItem?.variant && cartItem?.variant?.bcId

					if (productMatch) {
						if (kitHasVariant || cartHasVariant) {
							// Both must have variant.bcId and match
							return kitItem.variant?.bcId && cartItem.variant?.bcId && kitItem.variant.bcId === cartItem.variant.bcId
						}
						// Neither has variant, or only product match matters
						return true
					}
					return false
				})

				if (matchIndex !== -1) {
					// Merge quantities, use kit item (preserving group)
					const mergedItem = { ...kitItem, quantity: kitItem.quantity + (cartItems[matchIndex].quantity || 0) }
					cartItems.splice(matchIndex, 1, mergedItem)
				} else {
					cartItems.push(kitItem)
				}
			}

			this.store.cart.cartItems = cartItems
			// this.store.showBag = true
			this.store.cart.staffNote = `Birth Kit: "${this.kit.name}".`
			console.log('staff note added: ', this.store.cart.staffNote)

			this.upsellItems = this.filterProducts(this.upsellItems)?.slice(0, 12)

			this.upsellModalOpen = true

			// Swal.fire({
			// 	title: 'The kit has been added to your bag.',
			// 	icon: 'success',
			// 	html: `
			//     <div class="ob-success-actions">
			//         <a class="ob-bulk-view-cart button button--primary" href="/cart.php">View Bag</a>
			//         <a class="ob-bulk-continue button button--secondary">Continue Shopping</a>
			//     </div>
			//     <h4 class="ob-upsell-products-title">You may also like these:</h4>
			//     <div class="ob-upsell-products product">${this.upsellProductsHTML}</div>`,
			// 	width: '55em;',
			// 	showCancelButton: false,
			// 	showConfirmButton: false,
			// 	onOpen: () => this.bindSuccessCartButtons(),
			// })
		},
		downloadContentOnPage() {
			localStorage.setItem('kit', JSON.stringify(this.kit))

			// Open the new window
			window.open('/birth-kit-order-form', '_blank')
		},
		async claimKit() {
			if (!this.store?.user?.id) {
				this.showLogin = true
				return
			}

			try {
				const response = await axios.post(`${this.store.api}/controllers/birthKit/claim`, {
					kitId: this.kit.id,
				})

				if (response.error) {
					Swal.fire({ text: response.error, icon: 'error' })
					return
				}

				Swal.fire({ text: 'Your claim request has been sent. Our team will review it and contact you soon.', icon: 'success' })
			} catch (error) {
				console.error('Error claiming kit:', error)
				Swal.fire({ text: 'There was an error submitting your claim. Please try again later.', icon: 'error' })
			}
		},
		variantsOutOfStock(product) {
			return product?.variants?.every((v) => v.inventoryLevel <= 0) && product.inventoryTracking === 'variant'
		},
		productAndVariantsOutOfStock(product) {
			return product?.outOfStock || this.variantsOutOfStock(product)
		},
		kitHasOutOfStockProducts(kit) {
			return kit?.products?.some((p) => this.productAndVariantsOutOfStock(p.product))
		},
		getOutOfStockProducts(kit) {
			return kit?.products?.filter((p) => this.productAndVariantsOutOfStock(p.product))
		},
		filterOutOfStock(products) {
			return products.filter((product) => {
				if (product.inventoryTracking !== 'variant') {
					if (!product.outOfStock) return true
					return false
				}

				return !product.variants?.every((v) => v.inventoryLevel <= 0)
			})
		},

		filterProducts(products) {
			// Get product IDs that are already in the cart
			const productIdsInCart = this.store.cart?.cartItems?.map?.((c) => c.item.bcId) || []

			// Filter out products that are already in the cart and out of stock products
			return this.filterOutOfStock(products.filter((product) => !productIdsInCart.includes(product.bcId)))
		},
	},
	computed: {
		editing() {
			return this.$route.path.includes('/edit')
		},
		optionalProducts() {
			return this.kit?.products?.filter((p) => p.optional && p.product?.id)
		},
		outOfStock() {
			// return this.kit?.products?.some((p) => {
			// 	p.product?.outOfStock || (p.product.inventoryTracking === 'variant' && p.variant?.inventoryLevel <= 0)
			// })
			return this.kit?.products?.some((p) => {
				return this.productAndVariantsOutOfStock(p.product)
			})
		},
		outOfStockProducts() {
			return this.kit?.products?.filter((p) => {
				return this.productAndVariantsOutOfStock(p.product)
			})
		},
		// Subtotal calculation (total before tax)
		subtotal() {
			return this.kit?.products?.reduce((total, p) => {
				let price = p.variant?.currentPrice || p.product?.currentPrice
				let rules = p.product?.bulkPricingRules
				if (rules?.length) console.log('rules', rules)
				for (let rule of rules) {
					console.log('rule', rule)
					if (p.quantity >= rule.quantityMin && (p.quantity <= rule.quantityMax || !rule.quantityMax)) {
						// price = rule.type === 'fixed' ? rule.amount : rule.type === 'price' ? price - rule.amount : price * rule.amount

						// fix for bulk pricing rule with type percent:
						price =
							rule.type === 'fixed'
								? rule.amount
								: rule.type === 'price'
									? price - rule.amount
									: rule.type === 'percent'
										? price * (1 - rule.amount / 100)
										: price

						console.log('price', price)
					}
				}
				return total + price * p.quantity
			}, 0)
		},
		// Texas State Sales Tax (8.21% of the subtotal)
		texasStateSalesTax() {
			const taxRate = 0.0821
			return this.subtotal * taxRate
		},
		// Total price including the tax
		totalPrice() {
			const totalWithTax = parseFloat(this.subtotal) + parseFloat(this.texasStateSalesTax)
			return totalWithTax // Return the total with tax formatted as a string
		},
		requiredOptionsSelected() {
			let productsWithMissingOptions = []
			let value = true
			this.kit.products
				.filter((p) => p.quantity > 0)
				.forEach((p) => {
					if (p.product?.productOptions?.length) {
						if (!p.variant?.id) {
							value = false
							productsWithMissingOptions.push(p?.product?.name)
						}
					}
				})
			return {
				value,
				products: productsWithMissingOptions,
			}
		},
	},
}
</script>
